import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as React from 'react'
import noop from 'lib/function/noop'
import isImeComposing from 'lib/dom/isImeComposing'
import styled from 'styled-components'
import clsx from 'clsx'

const ALWAYS_LTR_INPUT_TYPES = new Set([
  'email', // there are some websites that force LTR for emails (haaretz.co.il), some force RTL (Emirates/Etihad/Al-Jazeera)
  // we are keeping LTR here to prevent any shenanigans with weakly directional characters (., @ etc)
  'url',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
  // password: use document direction https://horizon.servicenow.com/workspace/components/now-input-password#internationalization
  // tel: browser default is LTR; PhoneInput isolates layout with direction: ltr on its wrapper
])

const ALWAYS_LTR_INPUT_MODES = new Set([
  'email', // see comment above
  'numeric',
  'decimal',
  // tel: browser default is LTR; PhoneInput isolates layout with direction: ltr on its wrapper
])

const FREE_TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
])

function getInputDirection({
  type,
  inputMode,
}: {
  type?: string
  inputMode?: HTMLInputElement['inputMode']
}): 'ltr' | 'auto' | undefined {
  if (type && ALWAYS_LTR_INPUT_TYPES.has(type)) {
    return 'ltr'
  }

  if (inputMode && ALWAYS_LTR_INPUT_MODES.has(inputMode)) {
    return 'ltr'
  }

  if (!type || FREE_TEXT_INPUT_TYPES.has(type)) {
    return 'auto'
  }

  return undefined
}

const Root = styled.input`
  &.is-uppercase {
    text-transform: uppercase;
  }

  /*
    Let placeholders pick bidi from their own text (Arabic copy vs Latin examples).
    Baseline support except IE 11 (we dropped IE 11 in 2022).
    https://caniuse.com/?search=unicode-bidi+plaintext
    https://aussiecommerce.atlassian.net/wiki/spaces/~5a9786750e48062940ee73d0/pages/2276982796/The+Case+of+IE
  */
  &::placeholder {
    unicode-bidi: plaintext;
  }

  /*
    dir="auto" orders typed text by its content, but alignment follows the page direction.
    Zero specificity so consumers can still set their own alignment.
  */
  :where(:dir(ltr) > &[dir="auto"]) {
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    text-align: left;
  }

  :where(:dir(rtl) > &[dir="auto"]) {
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    text-align: right;
  }

  /* An empty dir="auto" input has no text to detect, so it resolves to LTR and puts the caret on the left in RTL locales. */
  &[dir="auto"]:placeholder-shown {
    direction: inherit;
  }
`

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  onDirtyChange?: (isDirty: boolean) => void
  onErrorUpdate?: (input: HTMLInputElement) => void
  uppercase?: boolean
}

const Input = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    onDirtyChange = noop,
    onErrorUpdate = noop,
    onChange = noop,
    onInvalid = noop,
    onBlur = noop,
    onKeyDown = noop,
    type = 'text',
    inputMode,
    dir,
    uppercase,
    ...inputProps
  } = props
  const [isDirty, setDirty] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useImperativeHandle(ref, () => inputRef.current!)

  useEffect(() => {
    // value changed and has a value, must be a controlled component , re-evaluate our validity
    if (inputProps.value && inputRef.current) {
      updateErrorMessage(inputRef.current)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [inputProps.value])

  const onSetDirty = () => {
    if (!isDirty) {
      onDirtyChange(true)
      setDirty(true)
    }
  }

  const updateErrorMessage = (input: HTMLInputElement) => {
    const validity = input.validity
    if (validity) {
      onErrorUpdate(input)
    }
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateErrorMessage(e.currentTarget)
    onChange(e)
  }

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onSetDirty()
    updateErrorMessage(e.currentTarget)
    onBlur(e)
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(e)) return
    onKeyDown(e)
  }

  const onInputInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    onSetDirty()
    updateErrorMessage(e.currentTarget)
    onInvalid(e)
  }

  return (<Root
    {...inputProps}
    className={clsx(inputProps.className, clsx({ 'is-uppercase': uppercase }))}
    type={type}
    inputMode={inputMode}
    dir={dir ?? getInputDirection({ type, inputMode })}
    ref={inputRef}
    onChange={onInputChange}
    onBlur={onInputBlur}
    onInvalid={onInputInvalid}
    onKeyDown={onInputKeyDown}
  />)
})

Input.displayName = 'Input'

export default Input
